import React, { useState, useEffect, useRef } from 'react';
import { CheckCircle } from 'lucide-react';
import OtpInputGroup from './OtpInputGroup';
import { apiRequest } from '../../../services/api';

function maskPhone(phone) {
  const digits = (phone ?? '').replace(/\D/g, '');
  const last4 = digits.slice(-4);
  return `+1 (***) ***-${last4}`;
}

const PhoneOtpSection = ({ phone, onVerified }) => {
  const [otp, setOtp] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [countdown, setCountdown] = useState(0);
  const sendInitiated = useRef(false);

  useEffect(() => {
    if (sendInitiated.current) { return; }
    sendInitiated.current = true;
    sendCode();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (countdown <= 0) { return; }
    const id = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(id);
  }, [countdown]);

  const sendCode = async () => {
    try {
      setInfo('');
      await apiRequest('/api/v1/communications/phone-verification/send', {
        method: 'POST',
        body: JSON.stringify({ phone }),
      });
      setCountdown(60);
    } catch (err) {
      if (err.status === 409) {
        setInfo(err.message || 'A verification code was already sent. Please wait or click Resend.');
        setCountdown(60);
      } else {
        setError(err.message || 'Failed to send code. Please try again.');
      }
    }
  };

  const handleSubmit = async () => {
    if (otp.length !== 6 || submitting) { return; }
    setSubmitting(true);
    setError('');
    try {
      const result = await apiRequest('/api/v1/communications/phone-verification/verify', {
        method: 'POST',
        body: JSON.stringify({ code: otp }),
      });
      if (result.data?.verified) {
        onVerified();
      } else {
        const remaining = result.data?.attemptsRemaining ?? 0;
        setError(`Incorrect code. ${remaining} attempt${remaining !== 1 ? 's' : ''} remaining`);
        setOtp('');
      }
    } catch (err) {
      setError(err.message || 'Verification failed. Please try again.');
      setOtp('');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    setError('');
    setInfo('');
    setOtp('');
    await sendCode();
  };

  return (
    <div className="p-4 border border-gray-200 rounded-lg space-y-4">
      <div className="flex items-center gap-2">
        <div className="w-6 h-6 rounded-full border-2 border-emerald-500 flex items-center justify-center">
          <CheckCircle className="w-4 h-4 text-emerald-500" />
        </div>
        <div>
          <p className="font-semibold text-gray-900">Phone Verification</p>
          <p className="text-sm text-gray-500">Code sent to {maskPhone(phone)}</p>
        </div>
      </div>

      <OtpInputGroup
        value={otp}
        onChange={setOtp}
        disabled={submitting}
        inputPrefix="phone-otp"
      />

      {info && (
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" role="status">{info}</p>
      )}

      {error && (
        <p className="text-sm text-red-600" role="alert">{error}</p>
      )}

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={otp.length !== 6 || submitting}
          className="px-4 py-2 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {submitting ? 'Verifying…' : 'Verify'}
        </button>
        <button
          type="button"
          onClick={handleResend}
          disabled={countdown > 0}
          className="text-sm text-emerald-600 hover:text-emerald-700 disabled:text-gray-400 disabled:cursor-not-allowed"
        >
          {countdown > 0 ? `Resend in ${countdown}s` : 'Resend code'}
        </button>
      </div>
    </div>
  );
};

export default PhoneOtpSection;
